import React, { useEffect, useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router';

import useLayout from 'components/Layout/useLayout';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import Contents from 'components/Layout/Contents';
import PartnerService from 'api/PartnerService';
import Table from 'components/Table/Table';
import { TableContext } from '../../components/Table/TableContext';
import PartnerRowItem from './PartnerTable/PartnerRowItem';

import './Partner.scss';

export default function Partners() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [isDeleting, setDeleting] = useState(false);
  const [partnerParam, setPartnerParam] = useState('');
  const [queryParams, setQueryParams] = useState(
    new URLSearchParams(location?.search).get('search') || ''
  );
  const [partners, setPartners] = useState([]);
  const { scrollToTop } = useLayout();
  const { data, isLoading, isRefetching, isError, error, refetch } = useQuery(
    ['partners', search],
    () => PartnerService.getAll(search),
    {
      staleTime: 0,
      enabled: false,
    }
  );

  const mutation = useMutation((id) => PartnerService.deleteById(id), {
    onMutate: () => {
      setDeleting(true);
    },
    onSuccess: () => {
      setDeleting(false);
      queryClient.invalidateQueries(['partners', search]);
      refetch();
    },
  });

  const handleClickView = (tierId) => {
    navigate(`/partners/${tierId}/profile`, { state: { search } });
  };

  const handleClickEdit = (tierId) => {
    navigate(`/partners/${tierId}/edit`, { state: { search } });
  };

  const handleClickDelete = (partnerId) => {
    setOpen(true);
    setPartnerParam(partnerId);
  };

  const onDeleteConfirm = () => {
    mutation.mutate(partnerParam);
    setOpen(false);
  };

  let searchTimeout = 0;

  const onSearch = (e) => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      setSearch(e.target.value);
    }, 500);
  };

  useEffect(() => {
    queryClient.invalidateQueries(['partners', search]).then();
    refetch().then();
  }, [search, location]);

  useEffect(() => {
    const params = location.state?.search || '';

    if (params) {
      setQueryParams(params);
      setSearch(params);
    } else {
      setQueryParams('');
      setSearch('');
    }
  }, [location]);

  useEffect(() => {
    if (data?.data?.length) {
      const orderedPartners = data.data.sort((a, b) =>
        // eslint-disable-next-line no-nested-ternary
        a.name > b.name ? 1 : b.name > a.name ? -1 : 0
      );
      setPartners(orderedPartners);
    }
  }, [data]);
  useEffect(() => {
    scrollToTop();
  }, []);

  const renderSearch = () => (
    <div style={{ width: '300px' }}>
      <Form.Control
        size="sm"
        onChange={(e) => {
          setQueryParams(e.target.value);
          onSearch(e);
        }}
        type="text"
        placeholder="Enter keyword to search"
        value={queryParams}
        className="ms-2 mt-2"
      />
    </div>
  );

  const actions = useMemo(
    () => ({
      onClickView: handleClickView,
      onClickEdit: handleClickEdit,
      onClickDelete: handleClickDelete,
    }),
    []
  );

  return (
    <Contents>
      <TableContext.Provider value={actions}>
        <Table
          title="Partners"
          name="partner"
          columns={[
            'Name',
            'Title',
            'Tier',
            'City',
            'State',
            'Country',
            'Status',
          ]}
          dataSource={partners}
          isLoading={isLoading || isRefetching || isDeleting}
          isError={isError}
          creationLink="/partners/add"
          rowComponent={PartnerRowItem}
          tableControls={renderSearch()}
          renderError={() => (
            <>
              <h2 data-testid="Header-error">There is an error</h2>
              <p className="alert alert-danger">{error.message}</p>
            </>
          )}
          actionWidth="110px"
          enablePagination
          striped
          bordered
          hover
        />
      </TableContext.Provider>
      <ModalDialog
        open={open}
        setOpen={setOpen}
        header="Delete Partner"
        message="You are about to delete a partner. Are you sure about it?"
        onConfirm={onDeleteConfirm}
        dataTestid="Modal-deletePartner"
      />
    </Contents>
  );
}
